/**
 * Model Planner Service
 * Handles all CRUD operations for the Model Planner Wizard
 * Uses single JSONB column (projects.planner_data) for all storage
 */

import { supabase } from '../lib/supabase';

// Helper to generate unique IDs for section items
const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Default empty planner data structure
const getDefaultPlannerData = () => ({
  current_section: 'ponds',
  completed: false,
  sections: {}
});

export class ModelPlannerService {

  // ================================================================
  // CORE DATA ACCESS
  // ================================================================

  /**
   * Get planner data for a project
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Planner data object
   */
  static async getPlannerData(projectId) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('planner_data')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error getting planner data:', error);
        return { data: null, error };
      }

      // Return existing data or initialize with defaults
      const plannerData = data?.planner_data && Object.keys(data.planner_data).length > 0
        ? data.planner_data
        : getDefaultPlannerData();

      return { data: plannerData, error: null };
    } catch (error) {
      console.error('Error in getPlannerData:', error);
      return { data: null, error };
    }
  }

  /**
   * Update planner data for a project
   * @param {number} projectId - Project ID
   * @param {Object} plannerData - Complete planner data object
   * @returns {Promise<Object>}
   */
  static async updatePlannerData(projectId, plannerData) {
    try {
      const { data, error } = await supabase
        .from('projects')
        .update({ planner_data: plannerData })
        .eq('id', projectId)
        .select('planner_data')
        .single();

      if (error) {
        console.error('Error updating planner data:', error);
        return { data: null, error };
      }

      return { data: data.planner_data, error: null };
    } catch (error) {
      console.error('Error in updatePlannerData:', error);
      return { data: null, error };
    }
  }

  // ================================================================
  // SESSION MANAGEMENT (compatibility layer)
  // ================================================================

  /**
   * Get or create a planner session for a project
   * Returns a session-like object for compatibility with existing code
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Session object
   */
  static async getOrCreateSession(projectId) {
    try {
      const { data: plannerData, error } = await this.getPlannerData(projectId);

      if (error) {
        return { data: null, error };
      }

      // Build session-like object for compatibility
      const session = {
        id: projectId, // Use projectId as session ID
        project_id: projectId,
        current_section: plannerData.current_section || 'ponds',
        completed: plannerData.completed || false
      };

      return { data: session, error: null };
    } catch (error) {
      console.error('Error in getOrCreateSession:', error);
      return { data: null, error };
    }
  }

  /**
   * Update session metadata
   * @param {number} projectId - Project ID (was sessionId, but now same thing)
   * @param {Object} updates - Fields to update (current_section, completed)
   * @returns {Promise<Object>}
   */
  static async updateSession(projectId, updates) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { data: null, error: fetchError };

      // Update session fields
      if (updates.current_section !== undefined) {
        plannerData.current_section = updates.current_section;
      }
      if (updates.completed !== undefined) {
        plannerData.completed = updates.completed;
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      if (error) return { data: null, error };

      return {
        data: {
          id: projectId,
          project_id: projectId,
          ...updates
        },
        error: null
      };
    } catch (error) {
      console.error('Error in updateSession:', error);
      return { data: null, error };
    }
  }

  /**
   * Mark session as completed
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>}
   */
  static async markSessionComplete(projectId) {
    return this.updateSession(projectId, { completed: true });
  }

  /**
   * Get complete session data with all sections
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} Complete session with all section data
   */
  static async getCompleteSession(projectId) {
    try {
      const { data: plannerData, error } = await this.getPlannerData(projectId);

      if (error) {
        return { data: null, error };
      }

      const sections = plannerData.sections || {};

      // Helper to flatten item (spread answers to top level)
      const flattenItem = (item) => {
        if (!item) return null;
        const { answers, ...rest } = item;
        return { ...rest, ...(answers || {}) };
      };

      // Flatten all section items
      const flattenedSections = {};
      Object.keys(sections).forEach(sectionKey => {
        flattenedSections[sectionKey] = (sections[sectionKey] || []).map(flattenItem);
      });

      // Build session object for compatibility
      const session = {
        id: projectId,
        project_id: projectId,
        current_section: plannerData.current_section || 'ponds',
        completed: plannerData.completed || false
      };

      // Build complete data object with expected property names
      const completeData = {
        session,
        // Looped sections (arrays)
        ponds: flattenedSections['ponds'] || [],
        curbTypes: flattenedSections['curb'] || [],
        walls: flattenedSections['walls'] || [],
        adaRamps: flattenedSections['ada-ramps'] || [],
        // Single sections (first item or null)
        gradingSections: flattenedSections['grading']?.[0] || null,
        ditchesSwales: flattenedSections['ditches']?.[0] || null,
        sidewalks: flattenedSections['sidewalks']?.[0] || null,
        buildingPads: flattenedSections['building-pads']?.[0] || null,
        grateElevations: flattenedSections['grate-elevations']?.[0] || null,
        // Also include raw section_key mapping
        ...flattenedSections
      };

      return { data: completeData, error: null };
    } catch (error) {
      console.error('Error in getCompleteSession:', error);
      return { data: null, error };
    }
  }

  // ================================================================
  // SECTION DATA METHODS
  // ================================================================

  /**
   * Create a new section data item
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier (e.g., 'ponds', 'walls', 'curb')
   * @param {Object} answers - Question answers as key-value pairs
   * @param {number} itemOrder - Order for looped sections (default 0)
   * @returns {Promise<Object>}
   */
  static async createSectionData(projectId, sectionKey, answers = {}, itemOrder = 0) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { data: null, error: fetchError };

      // Initialize sections if needed
      if (!plannerData.sections) {
        plannerData.sections = {};
      }
      if (!plannerData.sections[sectionKey]) {
        plannerData.sections[sectionKey] = [];
      }

      // Create new item
      const newItem = {
        id: generateId(),
        order: itemOrder,
        answers: answers,
        completed: false
      };

      plannerData.sections[sectionKey].push(newItem);

      // Sort by order
      plannerData.sections[sectionKey].sort((a, b) => a.order - b.order);

      const { error } = await this.updatePlannerData(projectId, plannerData);
      if (error) return { data: null, error };

      // Return flattened item for compatibility
      return {
        data: {
          id: newItem.id,
          section_key: sectionKey,
          item_order: newItem.order,
          completed: newItem.completed,
          ...newItem.answers
        },
        error: null
      };
    } catch (error) {
      console.error('Error in createSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Get section data by ID
   * @param {number} projectId - Project ID
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>}
   */
  static async getSectionData(projectId, itemId) {
    try {
      const { data: plannerData, error } = await this.getPlannerData(projectId);
      if (error) return { data: null, error };

      // Search all sections for the item
      for (const [sectionKey, items] of Object.entries(plannerData.sections || {})) {
        const item = items.find(i => i.id === itemId);
        if (item) {
          return {
            data: {
              id: item.id,
              section_key: sectionKey,
              item_order: item.order,
              completed: item.completed,
              answers: item.answers,
              ...item.answers
            },
            error: null
          };
        }
      }

      return { data: null, error: { message: 'Item not found' } };
    } catch (error) {
      console.error('Error in getSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Get all section data items for a section
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier
   * @returns {Promise<Object>}
   */
  static async getAllSectionData(projectId, sectionKey) {
    try {
      const { data: plannerData, error } = await this.getPlannerData(projectId);
      if (error) return { data: null, error };

      const items = plannerData.sections?.[sectionKey] || [];

      // Return flattened items for compatibility
      const flattenedItems = items.map(item => ({
        id: item.id,
        section_key: sectionKey,
        item_order: item.order,
        completed: item.completed,
        answers: item.answers,
        ...item.answers
      }));

      return { data: flattenedItems, error: null };
    } catch (error) {
      console.error('Error in getAllSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Update section data answers
   * @param {number} projectId - Project ID
   * @param {string} itemId - Item ID
   * @param {Object} answers - New answers object
   * @returns {Promise<Object>}
   */
  static async updateSectionData(projectId, itemId, answers) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { data: null, error: fetchError };

      // Find and update the item
      let found = false;
      let updatedItem = null;
      let sectionKey = null;

      for (const [key, items] of Object.entries(plannerData.sections || {})) {
        const index = items.findIndex(i => i.id === itemId);
        if (index !== -1) {
          plannerData.sections[key][index].answers = answers;
          updatedItem = plannerData.sections[key][index];
          sectionKey = key;
          found = true;
          break;
        }
      }

      if (!found) {
        return { data: null, error: { message: 'Item not found' } };
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      if (error) return { data: null, error };

      return {
        data: {
          id: updatedItem.id,
          section_key: sectionKey,
          item_order: updatedItem.order,
          completed: updatedItem.completed,
          ...updatedItem.answers
        },
        error: null
      };
    } catch (error) {
      console.error('Error in updateSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Mark section data as completed
   * @param {number} projectId - Project ID
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>}
   */
  static async markSectionDataComplete(projectId, itemId) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { data: null, error: fetchError };

      // Find and mark complete
      let found = false;
      for (const items of Object.values(plannerData.sections || {})) {
        const item = items.find(i => i.id === itemId);
        if (item) {
          item.completed = true;
          found = true;
          break;
        }
      }

      if (!found) {
        return { data: null, error: { message: 'Item not found' } };
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      if (error) return { data: null, error };

      return { data: { id: itemId, completed: true }, error: null };
    } catch (error) {
      console.error('Error in markSectionDataComplete:', error);
      return { data: null, error };
    }
  }

  /**
   * Delete section data
   * @param {number} projectId - Project ID
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>}
   */
  static async deleteSectionData(projectId, itemId) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { error: fetchError };

      // Find and delete
      for (const [sectionKey, items] of Object.entries(plannerData.sections || {})) {
        const index = items.findIndex(i => i.id === itemId);
        if (index !== -1) {
          plannerData.sections[sectionKey].splice(index, 1);
          break;
        }
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      return { error };
    } catch (error) {
      console.error('Error in deleteSectionData:', error);
      return { error };
    }
  }

  /**
   * Delete all section data for a section
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier
   * @returns {Promise<Object>}
   */
  static async deleteAllSectionData(projectId, sectionKey) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { error: fetchError };

      if (plannerData.sections) {
        plannerData.sections[sectionKey] = [];
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      return { error };
    } catch (error) {
      console.error('Error in deleteAllSectionData:', error);
      return { error };
    }
  }

  /**
   * Get all section data for all sections
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>}
   */
  static async getAllSessionSectionData(projectId) {
    try {
      const { data: plannerData, error } = await this.getPlannerData(projectId);
      if (error) return { data: null, error };

      // Flatten all items from all sections
      const allItems = [];
      for (const [sectionKey, items] of Object.entries(plannerData.sections || {})) {
        items.forEach(item => {
          allItems.push({
            id: item.id,
            section_key: sectionKey,
            item_order: item.order,
            completed: item.completed,
            answers: item.answers
          });
        });
      }

      return { data: allItems, error: null };
    } catch (error) {
      console.error('Error in getAllSessionSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Upsert section data for single-type sections
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier
   * @param {Object} answers - Question answers
   * @returns {Promise<Object>}
   */
  static async upsertSingleSectionData(projectId, sectionKey, answers) {
    try {
      const { data: plannerData, error: fetchError } = await this.getPlannerData(projectId);
      if (fetchError) return { data: null, error: fetchError };

      // Initialize if needed
      if (!plannerData.sections) {
        plannerData.sections = {};
      }

      if (!plannerData.sections[sectionKey] || plannerData.sections[sectionKey].length === 0) {
        // Create new
        plannerData.sections[sectionKey] = [{
          id: generateId(),
          order: 0,
          answers: answers,
          completed: false
        }];
      } else {
        // Update existing (first item)
        plannerData.sections[sectionKey][0].answers = answers;
      }

      const { error } = await this.updatePlannerData(projectId, plannerData);
      if (error) return { data: null, error };

      const item = plannerData.sections[sectionKey][0];
      return {
        data: {
          id: item.id,
          section_key: sectionKey,
          item_order: item.order,
          completed: item.completed,
          ...item.answers
        },
        error: null
      };
    } catch (error) {
      console.error('Error in upsertSingleSectionData:', error);
      return { data: null, error };
    }
  }

  /**
   * Get the next available item_order for a looped section
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier
   * @returns {Promise<number>}
   */
  static async getNextItemOrder(projectId, sectionKey) {
    try {
      const { data: plannerData } = await this.getPlannerData(projectId);
      const items = plannerData?.sections?.[sectionKey] || [];

      if (items.length === 0) {
        return 0;
      }

      const maxOrder = Math.max(...items.map(item => item.order || 0));
      return maxOrder + 1;
    } catch (error) {
      console.error('Error in getNextItemOrder:', error);
      return 0;
    }
  }

  /**
   * Check if a section is complete
   * @param {number} projectId - Project ID
   * @param {string} sectionKey - Section identifier
   * @param {string} sectionType - 'looped' or 'single'
   * @returns {Promise<boolean>}
   */
  static async isSectionComplete(projectId, sectionKey, sectionType = 'single') {
    try {
      const { data: plannerData } = await this.getPlannerData(projectId);
      const items = plannerData?.sections?.[sectionKey] || [];

      if (items.length === 0) {
        return false;
      }

      if (sectionType === 'looped') {
        return items.every(item => item.completed === true);
      } else {
        return items[0].completed === true;
      }
    } catch (error) {
      console.error('Error in isSectionComplete:', error);
      return false;
    }
  }

  /**
   * Clear all planner data for a project (reset)
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>}
   */
  static async clearPlannerData(projectId) {
    try {
      const { error } = await this.updatePlannerData(projectId, getDefaultPlannerData());
      return { error };
    } catch (error) {
      console.error('Error in clearPlannerData:', error);
      return { error };
    }
  }
}

export default ModelPlannerService;
